import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmt = (s) => (s ? new Date(s).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }) : "—");
const why = (e) => (e?.response?.data?.detail?.blocked_by ? `Not queued: ${e.response.data.detail.blocked_by.join(", ").replaceAll("_", " ")}` : apiErr(e));

function CustomerPicker({ value, onPick }) {
  const [q, setQ] = useState("");
  const r = useQuery({ queryKey: ["bo-cust", q], enabled: q.length >= 2, queryFn: async () => (await api.get("/biz-outbound/customer-search", { params: { q } })).data });
  if (value) return <p className="text-sm" data-testid="cb-customer-picked">{value.name} {value.mobile} <button className="text-xs underline" onClick={() => onPick(null)} data-testid="cb-customer-clear">change</button></p>;
  return (
    <div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer by name or mobile" className="w-full rounded border px-2 py-1 text-sm" data-testid="cb-customer-search" />
      {(r.data || []).map((c) => <button key={c.id} onClick={() => onPick(c)} className="block w-full px-2 py-1 text-left text-sm hover:bg-slate-50" data-testid={`cb-customer-${c.id}`}>{c.name} · {c.mobile}</button>)}
    </div>
  );
}

function CallbackForm({ onDone }) {
  const [cust, setCust] = useState(null);
  const [note, setNote] = useState("");
  const [source, setSource] = useState("");
  const save = () => api.post("/biz-outbound/callback-requests", { customer_id: cust.id, note, source })
    .then(() => { toast.success("Callback request recorded"); setCust(null); setNote(""); setSource(""); onDone(); }).catch((e) => toast.error(apiErr(e)));
  return (
    <div className="space-y-2 rounded-lg border p-3" data-testid="cb-form">
      <p className="text-sm font-semibold">Record a customer's callback request</p>
      <CustomerPicker value={cust} onPick={setCust} />
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What they want to talk about" className="w-full rounded border px-2 py-1 text-sm" data-testid="cb-note" />
      <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="How they asked (e.g. inbound call today, asked for a call back)" className="w-full rounded border px-2 py-1 text-sm" data-testid="cb-source" />
      <Button size="sm" disabled={!cust || note.length < 3 || source.length < 3} onClick={save} data-testid="cb-save">Record request</Button>
      <p className="text-[11px] text-slate-500">Only record it when the customer asked. One callback, valid for 7 days.</p>
    </div>
  );
}

function Callbacks() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["bo-cbr"], queryFn: async () => (await api.get("/biz-outbound/callback-requests")).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["bo-cbr"] }); qc.invalidateQueries({ queryKey: ["bo-calls"] }); };
  const queue = (id) => api.post(`/biz-outbound/callback-requests/${id}/queue`).then(() => { toast.success("Callback queued (not dialled)"); refresh(); }).catch((e) => toast.error(why(e)));
  return (
    <div className="space-y-2">
      <CallbackForm onDone={refresh} />
      <div className="overflow-x-auto"><table className="w-full text-xs" data-testid="cb-table">
        <thead><tr className="text-left text-slate-500"><th>Customer</th><th>Request</th><th>How they asked</th><th>Recorded</th><th>Status</th><th /></tr></thead>
        <tbody>{(q.data || []).map((r) => (
          <tr key={r.id} className="border-t" data-testid={`cb-row-${r.id}`}>
            <td>{r.customer_name} {r.mobile}</td><td>{r.note}</td><td>{r.source}</td><td>{fmt(r.created_at)}</td><td data-testid={`cb-state-${r.id}`}>{r.state}</td>
            <td>{r.state === "open" && <Button size="sm" variant="outline" onClick={() => queue(r.id)} data-testid={`cb-queue-${r.id}`}>Queue callback</Button>}</td>
          </tr>))}</tbody>
      </table></div>
      {q.data?.length === 0 && <p className="text-xs text-slate-500" data-testid="cb-empty">No callback requests.</p>}
    </div>
  );
}

function Invoices() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["bo-inv"], queryFn: async () => (await api.get("/biz-outbound/eligible-invoices")).data });
  const queue = (id) => api.post(`/biz-outbound/invoices/${id}/queue`).then(() => { toast.success("Billing clarification queued (not dialled)"); qc.invalidateQueries({ queryKey: ["bo-inv"] }); qc.invalidateQueries({ queryKey: ["bo-calls"] }); })
    .catch((e) => toast.error(why(e)));
  return (
    <div className="space-y-2" data-testid="inv-list">
      <p className="text-sm font-semibold">Billing or service clarification about a recent invoice</p>
      <p className="text-[11px] text-slate-500">Last 60 days only. Needs the customer's consent for this purpose recorded in the last 7 days. Shally only explains the bill; disputes go to your team.</p>
      <div className="overflow-x-auto"><table className="w-full text-xs" data-testid="inv-table">
        <thead><tr className="text-left text-slate-500"><th>Invoice</th><th>Customer</th><th>Date</th><th>Status</th><th /></tr></thead>
        <tbody>{(q.data || []).map((i) => (
          <tr key={i.id} className="border-t" data-testid={`inv-row-${i.id}`}>
            <td>{i.invoice_number || i.id.slice(-6)}</td><td>{i.customer_name}</td><td>{fmt(i.created_at)}</td><td>{i.status}</td>
            <td>{i.already_queued ? <span data-testid={`inv-queued-${i.id}`}>queued</span> : <Button size="sm" variant="outline" onClick={() => queue(i.id)} data-testid={`inv-queue-${i.id}`}>Queue clarification call</Button>}</td>
          </tr>))}</tbody>
      </table></div>
      {q.data?.length === 0 && <p className="text-xs text-slate-500" data-testid="inv-empty">No recent invoices.</p>}
    </div>
  );
}

export function ServiceRequestsTab({ rules }) {
  return (
    <div className="space-y-4" data-testid="bo-service-requests">
      <Callbacks />
      <Invoices />
      {rules && <details className="text-xs text-slate-600" data-testid="bo-consent-rules"><summary className="cursor-pointer font-semibold">Consent rules per call type</summary>
        <ul className="mt-1 list-disc pl-4">{Object.entries(rules).map(([k, v]) => <li key={k}><b>{k.replaceAll("_", " ")}</b>: {v}</li>)}</ul></details>}
    </div>
  );
}
